// The ONE way a job is cancelled (operator ruling 2026-10-07: industry standard, consistent).
//
// Every Cancel button (Jobs row menu, Jobs details drawer, job edit form, Field view, Calendar)
// opens components/CancellationModal and calls cancelWorkOrder. Before this there were five
// writers with three dialogs: the row menu asked no reason and cancelled any status, the drawer
// allowed only draft/sent/scheduled ("beta restriction"), Calendar had its own dialog that
// recorded no reason and cleared the technician.
//
// Which statuses can be cancelled is the DB's rule (is_valid_work_order_transition, mirrored by
// WORK_ORDER_STATUS_TRANSITIONS): anything up to and including completed. Invoiced / paid /
// closed jobs are not cancelled - their invoice is voided instead.

import { supabase } from '../utils/supabaseClient';
import calendarService from './CalendarService';
import JobPartsService from './JobPartsService';
import { WORK_ORDER_STATUS } from '../constants/statusEnums';
import { isStatusTransitionAllowed } from '../utils/statusHelpers';
import NotificationIntegrationService, { customerEmailOutcome } from './NotificationIntegrationService';
import { showToast } from '../components/Common/Toast';

export const canCancelWorkOrder = (status) =>
  isStatusTransitionAllowed(status, WORK_ORDER_STATUS.CANCELLED);

/**
 * @param {object} p
 * @param {string} p.workOrderId
 * @param {string} p.companyId
 * @param {string} p.status       current status (checked against the transition rule)
 * @param {string} p.userId       users.id of who cancelled (FK target of cancelled_by)
 * @param {{reason: string, notes?: string, initiatedBy?: string}} p.cancellation  from CancellationModal
 * @param {object} [p.alsoSave]   other work_orders fields edited alongside (job edit form only)
 */
export const cancelWorkOrder = async ({ workOrderId, companyId, status, userId, cancellation, alsoSave = {} }) => {
  if (status && !canCancelWorkOrder(status)) {
    throw new Error(`A job that is ${String(status).replace(/_/g, ' ')} can't be cancelled. Void its invoice instead.`);
  }

  // The booked visit, read before the cancel clears it: the customer is told which appointment
  // was cancelled (PRODUCT_DECISIONS "Cancel and reschedule email the customer", 2026-10-24).
  const { data: before } = await supabase
    .from('work_orders')
    .select('status, scheduled_start, scheduled_end')
    .eq('id', workOrderId)
    .eq('company_id', companyId)
    .maybeSingle();

  // cancelled_at is stamped by the DB trigger. The assigned technician is kept (it is the
  // stable "whose job was this" signal, CLAUDE.md section 8); the schedule is cleared so the slot frees up.
  const { data: cancelledRows, error } = await supabase
    .from('work_orders')
    .update({
      ...alsoSave,
      status: WORK_ORDER_STATUS.CANCELLED,
      cancelled_by: userId || null,
      cancellation_reason: cancellation?.reason || null,
      cancellation_notes: cancellation?.notes?.trim() || null,
      cancellation_initiated_by: cancellation?.initiatedBy || null,
      scheduled_start: null,
      scheduled_end: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', workOrderId)
    .eq('company_id', companyId)
    .select('id');
  if (error) throw error;
  // An update that matches no row (RLS, wrong company, deleted job) returns no error, so the
  // modal reported "cancelled" while the job was untouched.
  if (!cancelledRows || cancelledRows.length === 0) {
    throw new Error('The job could not be cancelled — it was not found or you do not have access to it.');
  }

  await releaseCancelledWorkOrder({ workOrderId, companyId, userId });

  // Email the customer when a booked visit is cancelled, the same way booking emails them.
  // Only a visit that was booked and not yet done: a quote, an unscheduled job or a completed
  // job has no appointment to call off. Not awaited - the job IS cancelled; the outcome is
  // shown as its own message, and a failed email never undoes the cancel.
  if (before?.scheduled_start && before.status !== WORK_ORDER_STATUS.COMPLETED) {
    notifyCustomerOfCancel(companyId, workOrderId, { start: before.scheduled_start, end: before.scheduled_end });
  }
};

const notifyCustomerOfCancel = async (companyId, workOrderId, previous) => {
  try {
    const outcome = customerEmailOutcome(
      await NotificationIntegrationService.notifyJobCancelled(companyId, { id: workOrderId }, previous)
    );
    if (!outcome) return; // in-app notifications switched off: no email attempted, as on booking
    if (outcome.sent) {
      showToast('info', 'Customer notified of the cancellation by email');
    } else {
      showToast('warning', `Cancelled, but the customer email could not be sent (${outcome.reason || 'unknown error'})`);
    }
  } catch (e) {
    showToast('warning', `Cancelled, but the customer email could not be sent (${e?.message || e})`);
  }
};

/**
 * What follows a cancel, for a work order that is ALREADY cancelled. Shared by cancelWorkOrder
 * and the end_recurring_plan RPC path (RecurringJobsService.endRecurringJob), which cancels a
 * plan's upcoming visits in the database and then runs this for each one.
 */
export const releaseCancelledWorkOrder = async ({ workOrderId, companyId, userId }) => {
  // Remove the calendar entry so no ghost event (or busy slot) is left behind.
  await calendarService.deleteScheduleEventByWorkOrder(workOrderId, companyId);

  // Stock only leaves the shelf at completion (JobPartsService), so a cancelled job took none.
  // Parts ALLOCATED to it are held (reserved): give the hold back so they show as available again.
  // Non-blocking: the job IS cancelled at this point.
  try {
    await JobPartsService.releaseReservations(workOrderId, companyId, userId, 'Allocation released: job cancelled');
  } catch (e) {
    console.warn('Releasing allocated parts on cancel failed:', e?.message || e);
  }
};
